// Swept AABB collision against the voxel grid. Resolves one axis at a time so
// entities slide along walls instead of sticking. Shared by player and mobs.

import { Block, isSolid } from './blocks';
import { Vec3 } from './math';
import { World } from './world';

export interface AABBEntity {
  /** Position is the centre of the box on X/Z and the feet on Y. */
  pos: Vec3;
  vel: Vec3;
  width: number;
  height: number;
  onGround: boolean;
}

function collidesAt(
  world: World,
  px: number,
  py: number,
  pz: number,
  w: number,
  h: number,
): boolean {
  const half = w / 2;
  const minX = Math.floor(px - half);
  const maxX = Math.floor(px + half);
  const minY = Math.floor(py);
  const maxY = Math.floor(py + h);
  const minZ = Math.floor(pz - half);
  const maxZ = Math.floor(pz + half);
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        if (isSolid(world.getBlock(x, y, z))) return true;
      }
    }
  }
  return false;
}

/**
 * Advance an entity by its velocity * dt, resolving collisions per-axis.
 * Mutates entity.pos, entity.vel, entity.onGround.
 */
export function moveEntity(world: World, e: AABBEntity, dt: number): void {
  const dx = e.vel.x * dt;
  const dy = e.vel.y * dt;
  const dz = e.vel.z * dt;

  // Sub-step to avoid tunnelling at high speed (e.g. long falls).
  const maxStep = 0.4;
  const steps = Math.max(
    1,
    Math.ceil(Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz)) / maxStep),
  );
  const sx = dx / steps;
  const sy = dy / steps;
  const sz = dz / steps;

  e.onGround = false;
  for (let i = 0; i < steps; i++) {
    // X axis
    if (sx !== 0) {
      const nx = e.pos.x + sx;
      if (!collidesAt(world, nx, e.pos.y, e.pos.z, e.width, e.height)) {
        e.pos.x = nx;
      } else {
        e.vel.x = 0;
      }
    }
    // Z axis
    if (sz !== 0) {
      const nz = e.pos.z + sz;
      if (!collidesAt(world, e.pos.x, e.pos.y, nz, e.width, e.height)) {
        e.pos.z = nz;
      } else {
        e.vel.z = 0;
      }
    }
    // Y axis
    if (sy !== 0) {
      const ny = e.pos.y + sy;
      if (!collidesAt(world, e.pos.x, ny, e.pos.z, e.width, e.height)) {
        e.pos.y = ny;
      } else {
        if (sy < 0) e.onGround = true;
        e.vel.y = 0;
      }
    }
  }
}

/** True if any part of the entity box overlaps a water block. */
export function isInWater(world: World, e: AABBEntity): boolean {
  const half = e.width / 2;
  const minX = Math.floor(e.pos.x - half);
  const maxX = Math.floor(e.pos.x + half);
  const minY = Math.floor(e.pos.y);
  const maxY = Math.floor(e.pos.y + e.height);
  const minZ = Math.floor(e.pos.z - half);
  const maxZ = Math.floor(e.pos.z + half);
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        if (world.getBlock(x, y, z) === Block.Water) return true;
      }
    }
  }
  return false;
}
